import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "../state/authStore";
import { useDesktopAgentStatus } from "../hooks/useDesktopAgentStatus";
import { useTeamStore } from "../state/teamStore";
import { useRunStore } from "../state/runStore";
import { api, ApiError } from "../utils/api";

/* ---------------------------------------------------------------------------
   Inline style helpers
   --------------------------------------------------------------------------- */
const sectionHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  padding: "0.85rem 1.25rem",
  borderBottom: "1px solid var(--rule)",
  background: "transparent",
};

const sectionStripeStyle: React.CSSProperties = {
  width: "4px",
  height: "18px",
  borderRadius: "2px",
};

const sectionTitleStyle: React.CSSProperties = {
  fontFamily: "var(--font-display)",
  fontSize: "1.05rem",
  fontWeight: 700,
  letterSpacing: "-0.01em",
  color: "var(--text-strong)",
};

const fieldLabelStyle: React.CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: "0.85rem",
  fontWeight: 500,
  color: "var(--text-secondary)",
  marginBottom: "0.4rem",
};

const readOnlyValueStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontVariantNumeric: "tabular-nums",
  fontSize: "0.92rem",
  color: "var(--text-secondary)",
  padding: "0.68rem 0.9rem",
  background: "var(--ink-input)",
  border: "1px solid var(--rule-faint)",
  borderRadius: "var(--r-sm)",
  letterSpacing: "0.02em",
};

/* ---------------------------------------------------------------------------
   Upload bay sub-components (moved from Dashboard)
   --------------------------------------------------------------------------- */

function DashedBorder({ active }: { active: boolean }) {
  const color = active ? "var(--live-edge)" : "var(--live-soft)";
  return (
    <svg
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="1"
        y="1"
        width="calc(100% - 2px)"
        height="calc(100% - 2px)"
        rx="12"
        ry="12"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeDasharray="10 6"
      />
    </svg>
  );
}

function UploadBayIcon({ active }: { active: boolean }) {
  const mainColor = active ? "var(--live)" : "var(--live-edge)";
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
      <path d="M8 4H4V8" stroke={mainColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M32 4H36V8" stroke={mainColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 36H4V32" stroke={mainColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M32 36H36V32" stroke={mainColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M20 28V14M20 14L14 20M20 14L26 20"
        stroke={mainColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={active ? 1 : 0.6}
      />
    </svg>
  );
}

/* ===========================================================================
   Settings Page
   =========================================================================== */
export default function Settings() {
  const { user, updatePreferences, checkAuth } = useAuthStore();
  const { activeTeamId } = useTeamStore();
  const { status: desktopStatus, loading: desktopStatusLoading } = useDesktopAgentStatus();
  const {
    uploading,
    uploadProgress,
    uploadIbt,
    pollJobStatus,
    fetchRuns,
    selectedTrackWeekId,
  } = useRunStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [unitSystem, setUnitSystem] = useState<"standard" | "metric">(
    user?.preferences?.units || "standard"
  );
  const [preferencesSaving, setPreferencesSaving] = useState(false);
  const [preferencesMessage, setPreferencesMessage] = useState<string | null>(null);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const dragCounter = useRef(0);
  // #228: avatar upload was a silent no-op on failure — validate + surface result.
  const [avatarMsg, setAvatarMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Route profile edits through api() (checks res.ok, unlike the old raw fetch that
  // treated a 4xx as success) and surface the result — a silent failure previously
  // reverted the field on the next checkAuth with no feedback.
  const saveProfile = async (patch: Record<string, string>) => {
    setProfileMsg(null);
    try {
      await api("/api/auth/profile", { method: "PATCH", body: JSON.stringify(patch) });
      setProfileMsg({ ok: true, text: "Saved" });
      checkAuth(true);
    } catch (err) {
      // Surface the server's reason for a 4xx (e.g. bio/website too long); generic
      // otherwise so a real outage reads as one.
      const text =
        err instanceof ApiError && err.status >= 400 && err.status < 500
          ? err.message
          : "Couldn't save. Check your connection and try again.";
      setProfileMsg({ ok: false, text });
    }
  };

  const handleAvatarFile = async (file: File) => {
    setAvatarMsg(null);
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      setAvatarMsg({ ok: false, text: "Use a JPG or PNG image." });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarMsg({ ok: false, text: "That image is over 5 MB. Pick a smaller one." });
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setAvatarBusy(true);
    try {
      const res = await fetch("/api/auth/avatar", {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (res.ok) {
        await res.json().catch(() => null);
        await checkAuth(true);
        setAvatarMsg({ ok: true, text: "Profile picture updated." });
      } else {
        setAvatarMsg({
          ok: false,
          text:
            res.status === 413
              ? "That image is over the size limit."
              : res.status === 400
                ? "That file isn't a valid JPG or PNG."
                : "Upload failed. Please try again.",
        });
      }
    } catch {
      setAvatarMsg({ ok: false, text: "Upload failed. Check your connection and try again." });
    } finally {
      setAvatarBusy(false);
    }
  };

  const pickAvatar = () => {
    if (avatarBusy) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png,image/jpeg";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) void handleAvatarFile(file);
    };
    input.click();
  };

  useEffect(() => {
    setUnitSystem(user?.preferences?.units || "standard");
  }, [user?.preferences?.units]);

  const handleUnitChange = useCallback(
    async (unit: "standard" | "metric") => {
      if (preferencesSaving || unit === unitSystem) return;
      setUnitSystem(unit);
      setPreferencesSaving(true);
      setPreferencesMessage(null);
      try {
        await updatePreferences({ units: unit });
        setPreferencesMessage("Preferences saved.");
      } catch (err) {
        setUnitSystem(user?.preferences?.units || "standard");
        setPreferencesMessage(
          err instanceof Error ? err.message : "Failed to save preferences."
        );
      } finally {
        setPreferencesSaving(false);
      }
    },
    [preferencesSaving, unitSystem, updatePreferences, user?.preferences?.units]
  );

  /* ── Upload handler ── */
  const handleFile = useCallback(
    async (file: File) => {
      if (!activeTeamId) return;
      if (!file.name.toLowerCase().endsWith(".ibt")) return;
      setUploadMessage(null);
      setUploadError(null);
      try {
        const jobId = await uploadIbt(file, activeTeamId);
        await pollJobStatus(jobId);
        await fetchRuns(activeTeamId, selectedTrackWeekId || undefined);
        setUploadMessage(`${file.name} uploaded and processed successfully.`);
      } catch (err) {
        console.error("Upload failed:", err);
        setUploadError(err instanceof Error ? err.message : "Upload failed.");
      }
    },
    [activeTeamId, uploadIbt, pollJobStatus, fetchRuns, selectedTrackWeekId]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  };

  /* ── Drag handlers ── */
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      dragCounter.current = 0;
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  return (
    <div className="page fade-in">
      <header className="editorial-header">
        <div className="editorial-meta">
          <div className="editorial-meta-left">
            <span className="eyebrow">Account</span>
          </div>
        </div>
        <h1 className="editorial-title">Settings</h1>
        <p className="editorial-dek">Profile, preferences, and manual upload</p>
      </header>

      {/* ================================================================
          PROFILE
          ================================================================ */}
      <div
        className="card"
        style={{
          marginBottom: "1.75rem",
          padding: 0,
          overflow: "hidden",
          border: "1px solid var(--live-soft)",
        }}
      >
        <div style={sectionHeaderStyle}>
          <div style={{ ...sectionStripeStyle, background: "var(--live)" }} />
          <h3 style={sectionTitleStyle}>Profile</h3>
        </div>

        <div style={{ padding: "1.25rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          {/* Avatar */}
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <button
              type="button"
              onClick={pickAvatar}
              disabled={avatarBusy}
              aria-label="Change profile picture"
              style={{
                width: 64, height: 64, borderRadius: "50%", padding: 0,
                background: user?.avatarUrl ? `url(${user.avatarUrl}) center/cover` : "var(--ink-3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: avatarBusy ? "wait" : "pointer", border: "2px solid var(--rule)",
                fontSize: "1.5rem", fontFamily: "var(--font-display)", fontWeight: 700,
                color: "var(--text-muted)", transition: "border-color 0.2s",
              }}
            >
              {!user?.avatarUrl && (user?.displayName?.[0]?.toUpperCase() || "?")}
            </button>
            <div>
              <div style={{ fontFamily: "var(--font-body)", fontSize: "0.8125rem", color: "var(--text-muted)" }}>
                Click to upload a profile picture
              </div>
              <div style={{ fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums", fontSize: "0.8125rem", color: "var(--text-muted)", opacity: 0.6, marginTop: "0.15rem" }}>
                Max 5 MB, JPG/PNG
              </div>
              <div
                role="status"
                aria-live="polite"
                style={{
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  marginTop: "0.3rem",
                  minHeight: "1.1rem",
                  color:
                    avatarMsg && !avatarBusy
                      ? avatarMsg.ok
                        ? "var(--flag-green)"
                        : "var(--flag-red)"
                      : "var(--text-secondary)",
                }}
              >
                {avatarBusy ? "Uploading…" : avatarMsg?.text ?? ""}
              </div>
            </div>
          </div>

          {/* Display Name */}
          <div>
            <div style={fieldLabelStyle}>Display name</div>
            <input
              className="input"
              defaultValue={user?.displayName || ""}
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (val && val !== user?.displayName) void saveProfile({ displayName: val });
              }}
              style={{ maxWidth: 320 }}
            />
          </div>

          {/* Bio */}
          <div>
            <div style={fieldLabelStyle}>About me</div>
            <textarea
              className="input"
              rows={3}
              defaultValue={user?.bio || ""}
              placeholder="Tell your teammates a bit about yourself..."
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (val !== (user?.bio || "")) void saveProfile({ bio: val });
              }}
              style={{ maxWidth: 480, resize: "vertical" }}
            />
          </div>

          {/* Website */}
          <div>
            <div style={fieldLabelStyle}>Website</div>
            <input
              className="input"
              type="url"
              defaultValue={user?.website || ""}
              placeholder="https://..."
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (val !== (user?.website || "")) void saveProfile({ website: val });
              }}
              style={{ maxWidth: 380 }}
            />
          </div>

          {profileMsg && (
            <div style={{ fontSize: "0.8125rem", color: profileMsg.ok ? "var(--flag-green)" : "var(--flag-red)" }}>
              {profileMsg.text}
            </div>
          )}

          {/* Read-only fields */}
          <div style={{ display: "flex", gap: "2rem", paddingTop: "0.5rem", borderTop: "1px solid var(--rule-faint)" }}>
            <div>
              <div style={fieldLabelStyle}>iRacing Customer ID</div>
              <div style={readOnlyValueStyle}>{user?.iracingCustId || "\u2014"}</div>
            </div>
            <div>
              <div style={fieldLabelStyle}>Email</div>
              <div style={readOnlyValueStyle}>{user?.email || "\u2014"}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================
          PREFERENCES
          ================================================================ */}
      <div
        className="card"
        style={{
          marginBottom: "1.75rem",
          padding: 0,
          overflow: "hidden",
          border: "1px solid var(--live-soft)",
        }}
      >
        <div style={sectionHeaderStyle}>
          <div style={{ ...sectionStripeStyle, background: "var(--live)" }} />
          <h3 style={sectionTitleStyle}>Preferences</h3>
        </div>

        <div style={{ padding: "1.25rem" }}>
          <div style={fieldLabelStyle}>Unit system</div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {(["standard", "metric"] as const).map((unit) => (
              <button
                key={unit}
                onClick={() => void handleUnitChange(unit)}
                disabled={preferencesSaving}
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "0.88rem",
                  fontWeight: 500,
                  padding: "0.5rem 1.2rem",
                  borderRadius: "6px",
                  cursor: "pointer",
                  border:
                    unitSystem === unit
                      ? "1px solid var(--live-edge)"
                      : "1px solid var(--rule)",
                  background:
                    unitSystem === unit
                      ? "var(--live-soft)"
                      : "var(--ink-input)",
                  color:
                    unitSystem === unit
                      ? "var(--live)"
                      : "var(--text-secondary)",
                  boxShadow:
                    unitSystem === unit
                      ? "0 0 12px var(--live-glow)"
                      : "none",
                  transition: "all 0.2s cubic-bezier(0.22, 1, 0.36, 1)",
                  opacity: preferencesSaving ? 0.75 : 1,
                }}
              >
                {unit === "standard" ? "Standard (Imperial)" : "Metric"}
              </button>
            ))}
          </div>
          <div
            style={{
              marginTop: "0.6rem",
              fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
              fontSize: "0.8125rem",
              color: preferencesMessage
                ? preferencesMessage.includes("saved")
                  ? "var(--live)"
                  : "var(--flag-red)"
                : "var(--text-muted)",
            }}
          >
            {preferencesSaving
              ? "Saving preference…"
              : preferencesMessage || "Used for weather, fuel, and tire displays across the app."}
          </div>
        </div>
      </div>

      {/* ================================================================
          MANUAL UPLOAD
          ================================================================ */}
      <div
        className="card"
        style={{
          marginBottom: "1.75rem",
          padding: 0,
          overflow: "hidden",
          border: "1px solid var(--flag-yellow-soft)",
        }}
      >
        <div
          style={{
            ...sectionHeaderStyle,
            borderBottomColor: "var(--flag-yellow-soft)",
            background:
              "linear-gradient(90deg, var(--flag-yellow-soft) 0%, transparent 60%)",
          }}
        >
          <div
            style={{ ...sectionStripeStyle, background: "var(--flag-yellow)" }}
          />
          <h3 style={sectionTitleStyle}>Manual upload</h3>
        </div>

        <div style={{ padding: "1.25rem" }}>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: "0.82rem",
              color: "var(--text-muted)",
              marginBottom: "1rem",
              lineHeight: 1.5,
            }}
          >
            Fallback upload — use this if the desktop agent failed to capture a
            session.
          </p>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".ibt"
            onChange={handleInputChange}
            style={{ display: "none" }}
          />

          {/* Drop zone */}
          <div
            style={{
              position: "relative",
              borderRadius: "12px",
              padding: dragActive ? "2.5rem 2rem" : "2rem",
              background: dragActive
                ? "var(--live-soft)"
                : "var(--ink-1)",
              overflow: "hidden",
              transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
              cursor: uploading ? "default" : "pointer",
            }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            <DashedBorder active={dragActive} />

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.75rem",
                position: "relative",
                zIndex: 2,
              }}
            >
              <UploadBayIcon active={dragActive} />
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 600,
                  fontSize: "0.88rem",
                  letterSpacing: "0.02em",
                  color: "var(--text-strong)",
                }}
              >
                {uploading
                  ? `Processing ${uploadProgress}%`
                  : dragActive
                  ? "Release to upload"
                  : "Telemetry upload bay"}
              </div>
              <div
                style={{
                  fontFamily: "var(--font-body)",
                  fontWeight: 400,
                  fontSize: "0.8125rem",
                  color: "var(--text-muted)",
                }}
              >
                {uploading
                  ? "Parsing telemetry data…"
                  : dragActive
                  ? "Drop your .ibt file to begin processing"
                  : "Drag and drop .ibt files here, or click to browse"}
              </div>
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontVariantNumeric: "tabular-nums",
                  fontSize: "0.8125rem",
                  color: "var(--live)",
                  opacity: 0.75,
                }}
              >
                {uploading
                  ? "Processing telemetry"
                  : dragActive
                  ? "Receiving signal"
                  : ".ibt"}
              </div>
            </div>
          </div>

          {/* Progress bar */}
          {uploading && (
            <div
              style={{
                height: "3px",
                background: "var(--live-soft)",
                borderRadius: "2px",
                overflow: "hidden",
                marginTop: "0.75rem",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${uploadProgress}%`,
                  background: "var(--live)",
                  borderRadius: "2px",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          )}

          {(uploadMessage || uploadError) && (
            <div
              style={{
                marginTop: "0.9rem",
                padding: "0.75rem 0.9rem",
                borderRadius: "8px",
                border: uploadError
                  ? "1px solid var(--flag-red)"
                  : "1px solid var(--live-edge)",
                background: uploadError
                  ? "var(--flag-red-soft)"
                  : "var(--live-soft)",
                color: uploadError ? "var(--flag-red)" : "var(--live-strong)",
                fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
                fontSize: "0.8125rem",
                lineHeight: 1.6,
              }}
            >
              {uploadError || uploadMessage}
            </div>
          )}
        </div>
      </div>

      {/* ================================================================
          DESKTOP AGENT
          ================================================================ */}
      <div
        className="card"
        style={{
          marginBottom: "1.75rem",
          padding: 0,
          overflow: "hidden",
          border: "1px solid var(--live-soft)",
        }}
      >
        <div style={sectionHeaderStyle}>
          <div style={{ ...sectionStripeStyle, background: "var(--live)" }} />
          <h3 style={sectionTitleStyle}>Desktop Agent</h3>
        </div>

        <div style={{ padding: "1.25rem" }}>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: "0.82rem",
              color: "var(--text-muted)",
              marginBottom: "1rem",
              lineHeight: 1.5,
            }}
          >
            The StintSense desktop agent watches your iRacing telemetry folder and
            automatically uploads new sessions as they complete. Install it once
            and never worry about manual uploads again.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
            <div
              style={{
                padding: "0.9rem 1rem",
                borderRadius: "10px",
                background: "rgba(255,255,255,0.035)",
                border: "1px solid var(--rule-strong)",
              }}
            >
              <div style={{ ...fieldLabelStyle, marginBottom: "0.25rem" }}>Current status</div>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "1rem",
                  fontWeight: 700,
                  color: "var(--text-strong)",
                }}
              >
                {desktopStatusLoading
                  ? "Checking desktop agent…"
                  : desktopStatus?.status === "connected"
                  ? "Connected"
                  : desktopStatus?.status === "needs_attention"
                  ? "Needs attention"
                  : desktopStatus?.status === "offline"
                  ? "Offline"
                  : "Not installed"}
              </div>
              <div
                style={{
                  marginTop: "0.3rem",
                  fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
                  fontSize: "0.8125rem",
                  color: "var(--text-muted)",
                  lineHeight: 1.6,
                }}
              >
                {desktopStatus?.status === "connected"
                  ? "Your Windows agent is reporting in and ready to upload automatically."
                  : desktopStatus?.status === "needs_attention"
                  ? "The agent is installed, but it is not fully healthy. Open the app and turn it back on."
                  : desktopStatus?.status === "offline"
                  ? "The agent has been seen before, but it has not sent a recent heartbeat."
                  : "Install the Windows agent to automate telemetry uploads after each iRacing session."}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
              <Link to="/desktop" className="btn btn-primary">
                Open Desktop Agent Guide
              </Link>
              <span
                style={{
                  fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
                  fontSize: "0.8125rem",
                  color: "var(--text-muted)",
                  letterSpacing: "0.04em",
                }}
              >
                Windows only
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
